import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * The users inside one account, from the platform console.
 *
 * This screen existed as a hole rather than as a decision, and the hole had two
 * halves.
 *
 * The first: the server has long allowed a super-administrator to reset any
 * account's password — the tenant check in `updateUser` is explicitly bypassed
 * for that role, with a comment saying why. But `GET /api/users` lists the
 * caller's own account and nothing else, so those rows were never on screen and
 * their ids were never obtainable.
 *
 * The second, and worse: creating an account from the console created a tenant
 * row and no user at all. The console could make an account that nobody — not
 * even the person who made it — could ever sign into. Hence "add user" here.
 *
 * The password field is deliberately per-row and cleared after every save. A
 * single shared input above a list is how a password ends up set on the wrong
 * person.
 */
export function TenantUsers({
  tenantId,
  tenantName,
  onClose,
}: {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    email: '',
    displayName: '',
    password: '',
    role: 'ADMIN',
  });

  const detail = useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => api.adminTenant(tenantId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ['admin-tenant', tenantId],
    });
    void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
  };

  const save = useMutation({
    mutationFn: (userId: string) => api.updateUser(userId, { password }),
    onSuccess: (_result, userId) => {
      setPassword('');
      setEditing(null);
      setSaved(userId);
      refresh();
    },
  });

  const add = useMutation({
    mutationFn: () => api.addTenantUser(tenantId, draft),
    onSuccess: () => {
      setDraft({ email: '', displayName: '', password: '', role: 'ADMIN' });
      setAdding(false);
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.deleteUser(userId),
    onSuccess: refresh,
  });

  const users = detail.data?.tenant.users ?? [];
  const canAdd =
    draft.email.includes('@') && draft.displayName.trim().length > 0 && draft.password.length >= 6;

  return (
    <div className="card tenant-users">
      <div className="panel-head">
        <h3>
          {t('admin.usersOf')} {detail.data?.tenant.name ?? tenantName}
        </h3>
        <button type="button" className="ghost small" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      {detail.isError && <ErrorNotice error={detail.error} />}
      {detail.isLoading && <div className="loading">{t('common.loading')}</div>}

      {/* An account with nobody in it cannot be signed into at all, so say so
          rather than showing an empty table and leaving it to be worked out. */}
      {detail.data && users.length === 0 && (
        <p className="warn small">{t('admin.noUsersWarning')}</p>
      )}

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.user')}</th>
              <th>{t('account.role')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong dir="ltr">{user.email}</strong>
                  <br />
                  <span className="tiny muted">{user.displayName}</span>
                </td>
                <td>{user.role}</td>
                <td>
                  <span className={`badge ${user.isActive ? 'ok' : 'err'}`}>
                    {user.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td>
                  {editing === user.id ? (
                    <div className="row">
                      <input
                        type="text"
                        value={password}
                        dir="ltr"
                        autoFocus
                        placeholder={t('admin.newPassword')}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                      <button
                        type="button"
                        className="primary small"
                        disabled={password.length < 6 || save.isPending}
                        onClick={() => save.mutate(user.id)}
                      >
                        {save.isPending ? t('profile.saving') : t('common.save')}
                      </button>
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => {
                          setEditing(null);
                          setPassword('');
                        }}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  ) : (
                    <div className="row">
                      <button
                        type="button"
                        className="small"
                        onClick={() => {
                          setEditing(user.id);
                          setPassword('');
                          setSaved(null);
                        }}
                      >
                        <Icon.shield /> {t('admin.setPassword')}
                      </button>
                      {/* Deleting is refused by the server for anyone who
                          published or connected — that history is not erasable
                          by removing its author. The refusal says so. */}
                      <button
                        type="button"
                        className="danger small"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              t('admin.confirmDeleteUser').replace('{email}', user.email),
                            )
                          ) {
                            remove.mutate(user.id);
                          }
                        }}
                      >
                        <Icon.trash />
                      </button>
                      {saved === user.id && (
                        <span className="ok tiny">{t('admin.passwordSet')}</span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {save.isError && <ErrorNotice error={save.error} />}
      {remove.isError && <ErrorNotice error={remove.error} />}

      {adding ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (canAdd) add.mutate();
          }}
        >
          <label htmlFor="tu-email">{t('login.email')}</label>
          <input
            id="tu-email"
            type="email"
            dir="ltr"
            value={draft.email}
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
          />

          <label htmlFor="tu-name">{t('account.displayName')}</label>
          <input
            id="tu-name"
            value={draft.displayName}
            onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
          />

          <label htmlFor="tu-pass">{t('admin.newPassword')}</label>
          <input
            id="tu-pass"
            type="text"
            dir="ltr"
            value={draft.password}
            onChange={(event) => setDraft({ ...draft, password: event.target.value })}
          />

          {/*
            The role is chosen here and nowhere else in the product, because
            REVIEWER is on this list. A reviewer is offered the permissions Meta
            has not published — that is what lets them review them — so handing
            the role out is a decision about the whole installation, not about
            one tenant's staff.
          */}
          <label htmlFor="new-user-role">{t('account.role')}</label>
          <select
            id="new-user-role"
            value={draft.role}
            onChange={(event) => setDraft({ ...draft, role: event.target.value })}
          >
            <option value="ADMIN">{t('account.role.ADMIN')}</option>
            <option value="PUBLISHER">{t('account.role.PUBLISHER')}</option>
            <option value="OPERATOR">{t('account.role.OPERATOR')}</option>
            <option value="VIEWER">{t('account.role.VIEWER')}</option>
            <option value="REVIEWER">{t('account.role.REVIEWER')}</option>
          </select>
          {draft.role === 'REVIEWER' && <p className="muted tiny">{t('admin.reviewerHint')}</p>}

          <div className="row">
            <button type="submit" className="primary" disabled={!canAdd || add.isPending}>
              {add.isPending ? t('profile.saving') : t('account.addUser')}
            </button>
            <button type="button" className="ghost" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </button>
          </div>

          {add.isError && <ErrorNotice error={add.error} />}
        </form>
      ) : (
        <button type="button" className="small" onClick={() => setAdding(true)}>
          <Icon.plus /> {t('account.addUser')}
        </button>
      )}

      <p className="muted small">{t('admin.passwordHint')}</p>
    </div>
  );
}
